import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

import { table } from '@gitbook/doc-core';
import type { EditorContainer } from '@gitbook/slate-react';

import type { TableRecordKV } from '../../types';

interface HandleRowReorderProps {
    event: DragEndEvent;
    editor: EditorContainer;
    tableKey: string;
    records: TableRecordKV[];
    definitionId: string;
}

export function handleRowReorder({
    event,
    editor,
    tableKey,
    records,
    definitionId,
}: HandleRowReorderProps): void {
    const recordIds = records.map((r) => r.key);
    const { active, over } = event;
    if (!over) return;

    const oldIndex = recordIds.indexOf(active.id as string);
    const newIndex = recordIds.indexOf(over.id as string);

    // (1) Reorder the array in memory
    const finalKeys = arrayMove(recordIds, oldIndex, newIndex);

    // (2) Find where the active item ended up
    const finalPosition = finalKeys.indexOf(active.id as string);

    // (3) Determine the neighbors' orderIndex
    let before: string | undefined;
    let after: string | undefined;

    if (finalPosition === 0) {
        if (finalKeys.length > 1) {
            after = getRecordOrderIndex(finalKeys[1], records);
        }
    } else if (finalPosition === finalKeys.length - 1) {
        before = getRecordOrderIndex(finalKeys[finalPosition - 1], records);
    } else {
        before = getRecordOrderIndex(finalKeys[finalPosition - 1], records);
        after = getRecordOrderIndex(finalKeys[finalPosition + 1], records);
    }

    // (4) Move the record in the editor
    editor.change((change) => {
        table.moveRecord(change, tableKey, active.id as string, { before, after });

        table.moveTableSelection(change, tableKey, {
            recordId: String(active.id),
            // select the first cell of the row
            definitionId,
        });
    });
}

function getRecordOrderIndex(recordKey: string, records: TableRecordKV[]): string {
    const found = records.find((r) => r.key === recordKey);
    return found?.value.orderIndex ?? '';
}

export function handleColumnReorder({
    event,
    editor,
    tableKey,
    recordId,
}: {
    event: DragEndEvent;
    editor: EditorContainer;
    tableKey: string;
    recordId: string;
}) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
        return;
    }

    editor.change((change) => {
        // Get current view data
        const tableNode = editor.value.document.assertBlock(tableKey);
        const data = tableNode.data.toFrozenObject();
        const view = data.view;

        // Get the current columns array
        const columns = [...view.columns];

        // Find indices
        const activeIndex = columns.indexOf(String(active.id));
        const overIndex = columns.indexOf(String(over.id));

        if (activeIndex !== -1 && overIndex !== -1) {
            // Remove the active column
            const [removed] = columns.splice(activeIndex, 1);
            // Insert it at the new position
            columns.splice(overIndex, 0, removed);

            // Update the view with new column order
            table.setView(change, tableKey, {
                ...view,
                columns,
            });
        }

        table.moveTableSelection(change, tableKey, {
            recordId,
            definitionId: String(active.id),
        });
    });
}
